import { Platform } from 'react-native';
import { PlatformUtils } from './platformUtils';

const CANCEL_ERRORS = new Set(['user_cancel', 'system_cancel', 'app_cancel']);

export type BiometricAuthResult = {
  success: boolean;
  error?: string;
  warning?: string;
};

export function isBiometricCancelError(error?: string): boolean {
  return !!error && CANCEL_ERRORS.has(error);
}

export function getBiometricErrorMessage(
  error?: string,
  warning?: string
): string | null {
  if (!error || isBiometricCancelError(error)) {
    return null;
  }

  switch (error) {
    case 'missing_usage_description':
      return (
        warning ||
        'Biometrics are not configured in this app build. Rebuild and reinstall the app.'
      );
    case 'lockout':
      return 'Too many failed attempts. Unlock your device with passcode, then try again.';
    case 'not_enrolled':
      return 'No fingerprint or Face ID is enrolled on this device.';
    case 'not_available':
      return 'Biometric authentication is not available on this device.';
    case 'authentication_failed':
      return 'Biometric verification did not match. Please try again.';
    case 'passcode_not_set':
      return 'Set a device passcode before using fingerprint unlock.';
    default:
      return 'Biometric authentication failed. Please try again.';
  }
}

export async function hasBiometricHardware(): Promise<boolean> {
  if (Platform.OS === 'web') return false;

  const LocalAuthentication = await PlatformUtils.localAuth();
  if (!LocalAuthentication) return false;

  const compatible = await LocalAuthentication.hasHardwareAsync();
  const enrolled = await LocalAuthentication.isEnrolledAsync();
  return compatible && enrolled;
}

export async function authenticateWithBiometrics(
  promptMessage: string
): Promise<BiometricAuthResult> {
  if (Platform.OS === 'web') {
    return { success: false, error: 'not_available' };
  }

  const LocalAuthentication = await PlatformUtils.localAuth();
  if (!LocalAuthentication) {
    return { success: false, error: 'not_available' };
  }

  const compatible = await LocalAuthentication.hasHardwareAsync();
  if (!compatible) {
    return { success: false, error: 'not_available' };
  }

  const enrolled = await LocalAuthentication.isEnrolledAsync();
  if (!enrolled) {
    return { success: false, error: 'not_enrolled' };
  }

  const result = await LocalAuthentication.authenticateAsync({
    promptMessage,
    cancelLabel: 'Cancel',
    disableDeviceFallback: false,
  });

  if (result.success) {
    return { success: true };
  }

  return {
    success: false,
    error: result.error,
    warning: result.warning,
  };
}
